import React, { useState } from 'react';
import { Upload, FileText, Loader2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';

interface UploadChangeDocumentProps {
  projectId: number;
  onSuccess?: (result: any) => void;
}

const UploadChangeDocument: React.FC<UploadChangeDocumentProps> = ({ projectId, onSuccess }) => {
  const [sourceType, setSourceType] = useState<'transcript' | 'notes' | 'document' | 'manual'>('notes');
  const [sourceContent, setSourceContent] = useState('');
  const [sourceReference, setSourceReference] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [processMaps, setProcessMaps] = useState<any[]>([]);
  const [selectedProcessMapId, setSelectedProcessMapId] = useState<number | null>(null);
  const [loadingMaps, setLoadingMaps] = useState(true);

  // Load process maps on mount
  React.useEffect(() => {
    const loadProcessMaps = async () => {
      try {
        setLoadingMaps(true);
        const response = await api.get(`/api/process-maps/project/${projectId}`);
        const maps = response.data.process_maps || [];
        setProcessMaps(maps);

        // Auto-select Level 3 map (Operational) by default
        if (maps.length > 0 && !selectedProcessMapId) {
          const level3Map = maps.find((map: any) => map.level === 3);
          if (level3Map) {
            setSelectedProcessMapId(level3Map.id);
          } else {
            // Fallback to first map if Level 3 doesn't exist
            setSelectedProcessMapId(maps[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to load process maps:', error);
        toast.error('Failed to load process maps');
      } finally {
        setLoadingMaps(false);
      }
    };

    if (projectId) {
      loadProcessMaps();
    }
  }, [projectId, selectedProcessMapId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sourceContent.trim()) {
      toast.error('Please enter content to analyze');
      return;
    }

    if (!selectedProcessMapId) {
      toast.error('Please select a process level');
      return;
    }

    setIsProcessing(true);
    setResult(null);

    try {
      const response = await api.post('/api/analysis/parse-changes', {
        projectId,
        processMapId: selectedProcessMapId,
        sourceType,
        sourceContent: sourceContent.trim(),
        sourceReference: sourceReference.trim() || undefined,
        modelId: 'anthropic.claude-3-5-sonnet-20241022-v2:0'
      });

      setResult(response.data);
      toast.success(`Successfully parsed ${response.data.detected_changes?.length || 0} changes`);

      if (onSuccess) {
        onSuccess(response.data);
      }

      // Clear form after success
      setSourceContent('');
      setSourceReference('');

    } catch (error: any) {
      console.error('Parse changes error:', error);
      toast.error(error.response?.data?.error || 'Failed to parse changes');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center gap-3 mb-6">
        <Upload className="w-6 h-6 text-blue-600" />
        <h2 className="text-xl font-semibold text-gray-900">Upload Change Document</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Process Level Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Process Level *
            <span className="text-gray-500 font-normal ml-2">Select which level to apply changes to</span>
          </label>
          {loadingMaps ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading process maps...</span>
            </div>
          ) : processMaps.length === 0 ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800">
              No process maps found for this project. Please create a process map first.
            </div>
          ) : (
            <select
              value={selectedProcessMapId || ''}
              onChange={(e) => setSelectedProcessMapId(Number(e.target.value))}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select a level...</option>
              {processMaps.map((map) => (
                <option key={map.id} value={map.id}>
                  Level {map.level} - {map.name}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Source Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Source Type
          </label>
          <div className="grid grid-cols-4 gap-3">
            {(['transcript', 'notes', 'document', 'manual'] as const).map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => setSourceType(type)}
                className={`px-4 py-2 rounded-lg border-2 font-medium transition-colors ${
                  sourceType === type
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Source Reference */}
        <div>
          <label htmlFor="sourceReference" className="block text-sm font-medium text-gray-700 mb-2">
            Reference (Optional)
            <span className="text-gray-500 font-normal ml-2">e.g., Meeting date, Document name</span>
          </label>
          <input
            id="sourceReference"
            type="text"
            value={sourceReference}
            onChange={(e) => setSourceReference(e.target.value)}
            placeholder="e.g., Q4 Planning Meeting - Oct 7, 2025"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Source Content */}
        <div>
          <label htmlFor="sourceContent" className="block text-sm font-medium text-gray-700 mb-2">
            Content to Analyze *
          </label>
          <textarea
            id="sourceContent"
            value={sourceContent}
            onChange={(e) => setSourceContent(e.target.value)}
            placeholder={`Enter ${sourceType} content here...\n\nExample:\n"We need to add a new approval step after the initial review. Also, the payment processing step should be updated to include fraud detection checks."`}
            rows={12}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
          />
          <p className="mt-2 text-sm text-gray-500">
            Paste transcript, meeting notes, or describe process changes in detail.
          </p>
        </div>

        {/* Submit Button */}
        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={isProcessing || !sourceContent.trim() || !selectedProcessMapId || loadingMaps}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyzing with AI...
              </>
            ) : (
              <>
                <FileText className="w-5 h-5" />
                Parse Changes
              </>
            )}
          </button>

          {sourceContent.trim() && (
            <span className="text-sm text-gray-500">
              {sourceContent.trim().split(/\s+/).length} words
            </span>
          )}
        </div>
      </form>

      {/* Result Summary */}
      {result && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-medium text-green-900 mb-2">Analysis Complete</h3>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-green-700 font-medium">Changes Detected:</span>
                  <span className="ml-2 text-green-900">{result.detected_changes?.length || 0}</span>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Confidence Score:</span>
                  <span className="ml-2 text-green-900">{((result.impact_summary?.overall_confidence || 0) * 100).toFixed(0)}%</span>
                </div>
                <div>
                  <span className="text-green-700 font-medium">Affected Steps:</span>
                  <span className="ml-2 text-green-900">{result.impact_summary?.affected_steps || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Processing Info */}
      {isProcessing && (
        <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900 mb-1">AI Analysis in Progress</h3>
              <p className="text-sm text-blue-700">
                Using Claude AI to identify process changes, modifications, and additions.
                This may take 30-60 seconds depending on content length.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadChangeDocument;
